import type { CSSProperties, ReactElement } from 'react';

import type { BrewType } from '@/lib/brewTypes';

interface BrewIconProps {
  type: BrewType;
  className?: string;
}

// Brew glyphs copied verbatim from the Wolisho Illustration System
// ("03 - BREW & RITUAL" chapter). Each brew method maps to its vessel: espresso
// → the crema shot, pour over → the kettle, filter → the cone dripper. Colour
// comes from the surrounding `className` via `currentColor`; `--cut` is the
// negative-space colour (the surface the glyph sits on).
const PATHS: Record<BrewType, ReactElement> = {
  // Espresso — full crema shot with ears
  espresso: (
    <>
      <rect x="7" y="49" width="12" height="22" rx="4" fill="currentColor" />
      <rect x="101" y="49" width="12" height="22" rx="4" fill="currentColor" />
      <circle cx="60" cy="60" r="40" fill="none" stroke="currentColor" strokeWidth="7" />
      <circle cx="60" cy="60" r="31" fill="currentColor" />
    </>
  ),
  // Pour over — kettle
  pourover: (
    <>
      <path d="M46 52 H78 L93 97 C93.5 100 89 101 62 101 C35 101 30.5 100 31 97 Z" fill="currentColor" />
      <rect x="42" y="45" width="40" height="7" rx="3.5" fill="currentColor" />
      <circle cx="62" cy="40" r="6" fill="currentColor" />
      <path d="M34 82 C16 78 10 64 14 48 C17 36 16 30 6 30" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <path d="M80 56 H99 C104 56 106 58 106 63 V78" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
      <g fill="none" stroke="var(--cut)" strokeWidth="2.5" strokeLinecap="round">
        <path d="M46 67 C57 70 67 70 78 67" />
        <path d="M43 81 C56 84 68 84 81 81" />
      </g>
    </>
  ),
  // Filter — cone dripper
  filter: (
    <>
      <path d="M91.5 46 C115 47.5 112.5 68 76 68" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" />
      <rect x="4.5" y="26.5" width="96" height="11.5" rx="5.75" fill="currentColor" />
      <path d="M8.5 41 H96.5 L68 86.5 H37 Z" fill="currentColor" />
      <path d="M21.5 89 H84" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" />
    </>
  ),
};

export function BrewIcon({ type, className = '' }: BrewIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      style={{ '--cut': 'var(--color-wolisho-card)' } as CSSProperties}
      aria-hidden="true"
    >
      {PATHS[type]}
    </svg>
  );
}
